// import { EntryModel } from "../Entries/EntryInterfaces";
import { EntryCardProps } from "../../components/CheckoutEntryCard/CheckoutEntryCard";
import { EntryModel } from "../Entries/EntryInterfacesComplete";
import { DropdownOptions } from "../../components/FormFields/DropdownField";
import {
	ThemisBreakdownLine,
	ThemisTrace,
} from "../Admin/Themis/ThemisInterfaces";

export interface orderLineItem {
	id: number;
	entryId: number;
	entry: EntryModel[];
	title: string;
	isCampaign: boolean;
	totalAmount: number;
}

export interface OrderModel {
	id?: number;
	cartId?: number;
	cartRevision?: number;
	checkoutAttemptId?: string;
	currencyCode?: string;
	discountName?: string;
	discountCodeMask?: string;
	statusId: number;
	transactionType: TransactionType;
	cardInfo?: CreditCard;
	paymentDate?: string;
	paymentzMerchantTransactionId?: string;
	paymentzPaymentId?: string;
	paymentzTransactionStatus?: string;
	paymentzLastCheckedUtc?: string;
	paymentzTerminalStatusUtc?: string;
	paymentzSuccessEmailSentUtc?: string;
	paymentzFailureEmailSentUtc?: string;
	// Totals
	orderTotal: number;
	subtotal: number;
	discountTotal: number;
	feeTotal: number;
	taxTotal: number;

	// Pricing snapshot
	pricingBreakdown?: ThemisBreakdownLine[];
	pricingTraceJson?: ThemisTrace[];
	pricingRuleSetKey?: string;
	pricingSnapshotVersion?: number; // server default = 1
	pricingSnapshotUtcMs?: number;

	bankName?: string;
	bankCity?: string;
	checkNumber?: string;
	checkStatus?: CheckStatus;
	fullName: string;
	email: string;
	phone: string;
	companyId?: number;
	companyName: string;
	companyCountry: string;
	companyCity: string;
	companyState: string;
	companyAddress1: string;
	companyAddress2?: string;
	companyPostalCode: string;
	notes?: string;
	invoicePDF?: string;
	hasPhysicalComponent?: boolean;
	lineItems: OrderLineItem[];
	createdDate?: string;
	createdBy?: string;
	updatedDate?: string;
	modifiedBy?: string;
	modifiedByName?: string;
	purchaseOrder?: string;
	taxId?: string;
}

export interface OrderModelPdf {
	OrderId: number;
	PdfFile?: Promise<Blob> | null;
	IsError?: boolean;
	AuthCode?: string;
	AuthMessage?: string;
}

export enum TransactionType {
	Credit,
	Check,
	Bank,
	Submission,
	Paymentz,
	Offline,
}

export const TransactionTypeOptions: DropdownOptions[] = [
	{ value: TransactionType.Credit, label: "Credit" },
	{ value: TransactionType.Check, label: "Check" },
	{ value: TransactionType.Bank, label: "Bank" },
	{ value: TransactionType.Submission, label: "Submission" },
	{ value: TransactionType.Paymentz, label: "Online" },
	{ value: TransactionType.Offline, label: "Offline" },
];

export enum CheckStatus {
	NotReceived,
	Received,
	Deposited,
	Cleared,
	Declined,
}

export const CheckStatusOptions: DropdownOptions[] = [
	{ value: CheckStatus.NotReceived, label: "Not Received" },
	{ value: CheckStatus.Received, label: "Received" },
	{ value: CheckStatus.Deposited, label: "Deposited" },
	{ value: CheckStatus.Cleared, label: "Cleared" },
	{ value: CheckStatus.Declined, label: "Declined" },
];

export interface CreditCard {
	cardNumber: string;
	expirationDate: string;
	cardCode: string;
}

export interface OrderLineItem {
	id?: number;
	entryId: number;
	isCampaign: boolean;
	title?: string;

	totalLineAmount?: number;
	orderTotal?: number;
	subtotal?: number;
	discountTotal?: number;
	discountName?: string;
	feeTotal?: number;
	taxTotal?: number;

	// Pricing snapshot
	pricingBreakdown?: ThemisBreakdownLine[];
	pricingTraceJson?: ThemisTrace[];
	pricingRuleSetKey?: string;
	pricingSnapshotVersion?: number; // server default = 1
	pricingSnapshotUtcMs?: number;

	entry: EntryCardProps;
	programHasPhysicalComponent?: boolean;
	orderId?: number;
}

export enum CartStatus {
	Active = 1,
	CheckoutPending = 2,
	Converted = 3,
	Abandoned = 4,
}

export interface AppliedDiscount {
	discountId: number;
	discountCodeId: number;
	discountName: string;
	codeMask: string;
	discountType: string;
	value: number;
	amount: number;
	currencyCode: string;
	appliedByUserId: string;
	requiresReapply: boolean;
	expiresUtc: string;
}

export interface DiscountValidationError {
	code: string;
	message: string;
}

export interface CartLine {
	id: number;
	entryId: number;
	isCampaign: boolean;
	subtotal: number;
	discountTotal: number;
	feeTotal: number;
	taxTotal: number;
	total: number;
	pricingBreakdown: ThemisBreakdownLine[];
	entry?: EntryCardProps | null;
}

export interface CartModel {
	id: number;
	companyId: number;
	status: CartStatus;
	currencyCode: string;
	transactionType?: TransactionType | null;
	billingCountry?: string | null;
	billingState?: string | null;
	revision: number;
	checkoutAttemptId?: string | null;
	subtotal: number;
	discountTotal: number;
	feeTotal: number;
	taxTotal: number;
	total: number;
	pricingBreakdown: ThemisBreakdownLine[];
	lines: CartLine[];
	appliedDiscount?: AppliedDiscount | null;
	discountErrors: DiscountValidationError[];
}

export interface UpdateCartRequest {
	expectedRevision?: number;
	transactionType?: TransactionType;
	billingCountry?: string;
	billingState?: string;
}

export enum PaymentStatus {
	Incomplete,
	ReadyForCheckout,
	PaymentPending,
	Paid,
	Deleted,
	Abandoned,
	Refunded,
}

export const PaymentStatusOptions: DropdownOptions[] = [
	{ value: PaymentStatus.Incomplete, label: "Incomplete" },
	{ value: PaymentStatus.ReadyForCheckout, label: "Ready For Checkout" },
	{ value: PaymentStatus.PaymentPending, label: "Payment Pending" },
	{ value: PaymentStatus.Paid, label: "Paid" },
	{ value: PaymentStatus.Deleted, label: "Deleted" },
	{ value: PaymentStatus.Abandoned, label: "Abandoned" },
	{ value: PaymentStatus.Refunded, label: "Refunded" },
];

export enum ReviewStatus {
	Draft,
	ReadyForReview,
	OnHold,
	Failed,
	Updated,
	Passed,
	Withdrawn,
	InJudging,
	Completed,
	Released,
}

export const ReviewStatusOptions: DropdownOptions[] = [
	{ value: ReviewStatus.Draft, label: "Draft" },
	{ value: ReviewStatus.ReadyForReview, label: "Ready For Review" },
	{ value: ReviewStatus.OnHold, label: "On Hold" },
	{ value: ReviewStatus.Failed, label: "Failed" },
	{ value: ReviewStatus.Updated, label: "Updated" },
	{ value: ReviewStatus.Passed, label: "Passed" },
	{ value: ReviewStatus.Withdrawn, label: "Withdrawn" },
	{ value: ReviewStatus.InJudging, label: "In Judging" },
	{ value: ReviewStatus.Completed, label: "Completed" },
	{ value: ReviewStatus.Released, label: "Released" },
];
